import React from 'react';

import { Button, Icon } from '@tecma/ds';
import { Trans, useTranslation } from '@tecma/i18n';
import classNames from 'classnames';

import { APARTMENT_FEATURES_INFO, ORDERED_FEATURE_KEYS } from 'constants/apartmentFeatures';

import type { ApartmentFeatures } from '../CardApartment';
import type { Building } from 'types/configurations';

import './card-apartment-info.scss';

interface CardApartmentInfoRequiredProps {
  features: ApartmentFeatures;
}

// Optional Props
interface CardApartmentInfoOptionalProps {
  name?: string;
  typologyName?: string;
  building?: Building;
  title?: string;
  isQuoteSelected?: boolean;
  className?: string;
  onClickSeeAllFeatures?: () => void;
}

// Combined required and optional props to build the full prop interface
export interface CardApartmentInfoProps extends CardApartmentInfoRequiredProps, CardApartmentInfoOptionalProps {}

const CardApartmentInfo: React.FC<CardApartmentInfoProps> = ({
  name,
  typologyName,
  building,
  features,
  isQuoteSelected,
  className,
  title,
  onClickSeeAllFeatures,
}) => {
  const { t } = useTranslation();
  const cardApartmentInfoClassList = classNames('card-apartment-info', {
    'quote-selected': isQuoteSelected,
    [`${className}`]: className,
  });

  return (
    <div className={cardApartmentInfoClassList}>
      {building && (
        <div className='apartment-location'>
          {building?.zone && (
            <>
              <div className='zone'>{building.zone}</div>
              <Icon iconName='dot' filled size='small' />
            </>
          )}
          <div className='address'>{building.address}</div>
        </div>
      )}
      {typologyName && (
        <h2 className='apartment-name'>
          {t(`apartment-model.${typologyName}`, { defaultValue: typologyName })}
          <span>{name}</span>
        </h2>
      )}
      {title && <h3 className='apartment-title'>{title}</h3>}
      <div className='apartment-features'>
        {ORDERED_FEATURE_KEYS.map(
          (feature) =>
            features[feature as keyof ApartmentFeatures] !== undefined && (
              <div className='feature' key={feature}>
                <Icon size='small' iconName={APARTMENT_FEATURES_INFO[feature].iconName} />
                <span>
                  <Trans
                    i18nKey={APARTMENT_FEATURES_INFO[feature].label}
                    values={{
                      count: features[feature as keyof ApartmentFeatures],
                    }}
                    context={feature === 'floor' ? 'floor' : undefined}
                  />
                </span>
              </div>
            ),
        )}
      </div>
      {onClickSeeAllFeatures && (
        <Button
          className='apartment-all-features-button'
          onClick={onClickSeeAllFeatures}
          iconName='download'
          outlined
          size='small'
          color='primary'
        >
          {t('card-apartment.feature.see-all-features')}
        </Button>
      )}
    </div>
  );
};

export default React.memo(CardApartmentInfo);
